import { logger } from '@heidi/runtime/logger';
import { TemplateCategory } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  type CreatePDFTemplateProps,
  type TemplateResponse,
  GET_CONSULT_NOTE_TEMPLATES,
  GET_DOCUMENT_TEMPLATES,
  GET_TEMPLATES,
} from '../../contracts';
import { createTemplate } from '../api/templates';

/**
 * The compliance-rejection branch, severed (D2.7 remainder, ARCFE-1055 #18785) — the
 * `useMutateDuplicateTemplate` shape (ARCFE-1046), which is this hook's closest
 * sibling: same `createTemplate` endpoint, same `from_pdf` trigger, same
 * `ComplianceCheckFailedError` body.
 *
 * The app reaction is an analytics dispatch plus a modal atom write, so it
 * cannot live here — but the DECISION that a given error is a compliance
 * rejection is data, made by inspecting the response body. The port therefore
 * receives the already-classified signal, not the raw error.
 */
export interface CreatePDFTemplateComplianceRejection {
  complianceOutputLength: number | undefined;
  /**
   * The backend's `passed_compliance_check`, carried through UNINTERPRETED.
   * The legacy hook read it as `isWarning` and it selects BOTH the analytics
   * event and the modal — two app reactions — so the choice belongs to the
   * delegate and only the wire fact crosses.
   */
  passedComplianceCheck: boolean;
}

export interface UseMutateCreatePDFTemplatePorts {
  /**
   * Fires as the last statement of `onSuccess`, after the three template lists
   * are invalidated. Everything the legacy hook did from here on was an app
   * reaction: one analytics dispatch behind a `if (data)` runtime guard, and
   * the success toast — which is why the template crosses and the guard stays
   * with the dispatch it guards.
   */
  onCreated: (template: TemplateResponse) => void;
  /** Compliance rejection: analytics + one of the two modals, both app reactions. */
  onComplianceRejected: (rejection: CreatePDFTemplateComplianceRejection) => void;
  /** Every other failure: the log line and the toast. */
  onRequestError: (error: unknown) => void;
}

/**
 * Creates a FORM template from an uploaded PDF and refreshes the three lists it
 * can appear in.
 *
 * Headless. Deliberately does NOT route its failure through the package's
 * shared `handleTemplateComplianceError`: that helper adds a
 * `template_content_length` prop, a `template_id`, an `ERR_CANCELED` skip, a
 * V2-modal branch and two `posthog.isFeatureEnabled` reads (each emitting a
 * `$feature_flag_called` exposure event) that this flow has never had. Reusing
 * it would be a behaviour change wearing a de-duplication costume.
 */
export function useMutateCreatePDFTemplate(ports: UseMutateCreatePDFTemplatePorts) {
  const { onCreated, onComplianceRejected, onRequestError } = ports;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createTemplate'],
    mutationFn: async ({
      input,
      saveAnyway,
    }: {
      input: CreatePDFTemplateProps;
      saveAnyway: boolean;
    }) => {
      // Rides along from the legacy hook, re-pointed at the runtime logger —
      // the app logger is an `@/` import the package cannot make.
      logger.debug('Template creation input:', input);

      const data = await createTemplate({
        template: {
          // is_private: true,
          ...input,
          trigger: 'from_pdf',
          template_category: TemplateCategory.FORM_TEMPLATE,
          template_visibility: input.template_visibility,
          s3_object_key: input.s3_object_key,
          team_id: input.team_id,
          shared_team_ids: input.shared_team_ids,
          convert_to_json: false,
          problem_based_charting_enabled: false,
          form_fields: input.form_fields,
          source_template_id: input.source_template_id,
        },
        saveAnyway: saveAnyway,
      });

      return data;
    },
    onSuccess: async ({ data }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_TEMPLATES] }),
        queryClient.invalidateQueries({ queryKey: [GET_DOCUMENT_TEMPLATES] }),
        queryClient.invalidateQueries({ queryKey: [GET_CONSULT_NOTE_TEMPLATES] }),
      ]);

      onCreated(data);
    },
    onError: (error) => {
      if (error instanceof AxiosError && error.response?.data) {
        const errorData = error.response.data as {
          detail: {
            type: string;
            noncompliant_input: string;
            passed_compliance_check: boolean;
          };
        };
        if (errorData.detail.type === 'ComplianceCheckFailedError') {
          onComplianceRejected({
            complianceOutputLength: errorData.detail.noncompliant_input?.length,
            passedComplianceCheck: errorData.detail.passed_compliance_check,
          });
          return;
        }
      }
      onRequestError(error);
    },
  });
}
